import { useCallback, useEffect, useState } from 'react';

/** PWA 설치 배너 대상 플랫폼. standalone으로 이미 실행 중이거나 데스크톱이면 null */
export type InstallPromptPlatform = 'android' | 'ios' | null;

interface BeforeInstallPromptEvent extends Event {
  prompt: () => Promise<void>;
  userChoice: Promise<{ outcome: 'accepted' | 'dismissed'; platform: string }>;
}

function isStandaloneDisplayMode(): boolean {
  if (typeof window === 'undefined') return false;
  const navigatorWithStandalone = window.navigator as Navigator & { standalone?: boolean };
  return (
    window.matchMedia('(display-mode: standalone)').matches ||
    navigatorWithStandalone.standalone === true
  );
}

function isMobileUserAgent(): boolean {
  if (typeof navigator === 'undefined') return false;
  return /Android|iPhone|iPad|iPod/i.test(navigator.userAgent);
}

function detectPlatform(): Exclude<InstallPromptPlatform, null> | null {
  if (typeof navigator === 'undefined') return null;
  if (/Android/i.test(navigator.userAgent)) return 'android';
  if (/iPhone|iPad|iPod/i.test(navigator.userAgent)) return 'ios';
  return null;
}

interface UseInstallPromptResult {
  /** 이미 PWA(standalone)로 실행 중인지 */
  isStandalone: boolean;
  /** 모바일 브라우저(Android/iOS)에서 접근했는지 */
  isMobile: boolean;
  /** 설치 유도 대상 플랫폼. standalone이거나 데스크톱이면 null */
  platform: InstallPromptPlatform;
  /** Android에서 `beforeinstallprompt` 이벤트로 받은 설치 프롬프트를 표시. iOS/미지원 브라우저에서는 아무 동작도 하지 않음 */
  promptInstall: () => Promise<void>;
}

/**
 * "홈 화면에 추가" 배너 노출 여부 판단에 필요한 상태/로직만 담당하는 훅 (UI 없음).
 * Android는 `beforeinstallprompt` 이벤트를 가로채 나중에 `promptInstall()`로 재호출하고,
 * iOS는 해당 이벤트가 없으므로 배너 쪽에서 수동 안내 문구를 노출한다.
 */
export function useInstallPrompt(): UseInstallPromptResult {
  const [isStandalone] = useState(isStandaloneDisplayMode);
  const [isMobile] = useState(isMobileUserAgent);
  const [platform] = useState<InstallPromptPlatform>(() =>
    isMobileUserAgent() && !isStandaloneDisplayMode() ? detectPlatform() : null
  );
  const [deferredPrompt, setDeferredPrompt] = useState<BeforeInstallPromptEvent | null>(null);

  useEffect(() => {
    if (platform !== 'android') return;

    const handleBeforeInstallPrompt = (event: Event) => {
      event.preventDefault();
      setDeferredPrompt(event as BeforeInstallPromptEvent);
    };

    window.addEventListener('beforeinstallprompt', handleBeforeInstallPrompt);
    return () => {
      window.removeEventListener('beforeinstallprompt', handleBeforeInstallPrompt);
    };
  }, [platform]);

  const promptInstall = useCallback(async () => {
    if (!deferredPrompt) return;
    await deferredPrompt.prompt();
    await deferredPrompt.userChoice;
    setDeferredPrompt(null);
  }, [deferredPrompt]);

  return { isStandalone, isMobile, platform, promptInstall };
}
